import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getClientId } from "../../App";
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";

import { fetchUser, getUser, selectToken, UserSlice, useUser } from "../user/userSlice";
import { Project } from "../../ProjectExplorer";
import { addAlert, setCreateNew } from "../ui/uiSlice";
import { ng } from "@common/ngds";
import { createAppSlice } from "../../app/createAppSlice";
import { ProjectFile } from "../../ProjectExplorer";
import { useAuth0 } from "@auth0/auth0-react";
import { AppType } from "@common/types/sharedTypes";

interface UserSliceState {
  UserSlice: { token: string | null };
}

export const delProject = createAsyncThunk(
  "projects/delete",
  async (args: { token: string; id: number; guid?: string; type: AppType | null }, thunkAPI) => {
    const tempheaders = {
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": String(getClientId()),
    };

    const response = await fetch(`api/projects/${args.guid}/${encodeURIComponent(args.type ?? -1)}/${args.id}`, {
      method: "DELETE",
      headers: tempheaders,
    });

    if (!response.ok) {
      thunkAPI.dispatch(
        addAlert({
          id: crypto.randomUUID(), // eller egen ID-generator
          type: "danger",
          text: "Failed to delete project",
          status: "danger",
        }),
      );
      throw new Error("Failed to delete project");
    }
  },
);

export const delProjectAndFiles = createAsyncThunk(
  "projects/deleteProjectFIles",
  async (args: { token: string; projectguid: string }, thunkAPI) => {
    const tempheaders = {
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": String(getClientId()),
    };

    const response = await fetch(`api/projects/projectsAndFiles/${args.projectguid}`, {
      method: "DELETE",
      headers: tempheaders,
    });

    if (!response.ok) {
      thunkAPI.dispatch(
        addAlert({
          id: crypto.randomUUID(), // eller egen ID-generator
          type: "danger",
          text: "Failed to delete project and files",
          status: "danger",
        }),
      );
      throw new Error("Failed to delete project");
    }
  },
);

export const delProjects = createAsyncThunk(
  "projects/deletes",
  async (args: { projects: { id: number; type: AppType; guid: string }[]; token: string }, thunkAPI) => {
    const tempheaders = {
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": String(getClientId()),
    };

    const response = await fetch(`api/projects/deleteList`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...tempheaders },
      body: JSON.stringify(args.projects),
    });

    if (!response.ok) {
      thunkAPI.dispatch(
        addAlert({
          id: crypto.randomUUID(), // eller egen ID-generator
          type: "danger",
          text: "Failed to delete projects",
          status: "danger",
        }),
      );
      throw new Error("Failed to delete project");
    }

    thunkAPI.dispatch(fetchUser({ token: args.token }));
  },
);

export const copyProjectFile = createAsyncThunk(
  "projects/copy",
  async (args: { id: string; guid: string; token: string; type: AppType; call: (name: string) => void }, thunkAPI) => {
    let state = thunkAPI.getState() as UserSliceState;
    const tempheaders = {
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": String(getClientId()),
    };
    const response = await fetch(`api/projects/copy`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...tempheaders },
      body: JSON.stringify({ id: args.id, guid: args.guid, type: args.type }),
    });

    if (!response.ok) {
      thunkAPI.dispatch(
        addAlert({
          id: crypto.randomUUID(), // eller egen ID-generator
          type: "danger",
          text: "Failed to copy project",
          status: "danger",
        }),
      );
      throw new Error("Failed to copy project");
    }
    let jsonObj = await response.json();
    args.call(jsonObj.name);
    thunkAPI.dispatch(setCreateNew(null));
    thunkAPI.dispatch(fetchUser({ token: args.token }));
  },
);

export const uploadProject = createAsyncThunk(
  "projects/upload",
  async (
    args: {
      data: FormData;
      projectName: string | null;
      projectGUID: string | null;
      token: string;
      callback: (name: string) => void;
    },
    thunkAPI,
  ) => {
    const tempheaders = {
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": String(getClientId()),
    };
    if (args.projectName) {
      let state = thunkAPI.getState();

      args.data.append("projectName", args.projectName);
    }
    if (args.projectGUID) {
      args.data.append("projectGUID", args.projectGUID);
    }
    const response = await fetch("/api/projects/upload", {
      method: "POST",
      body: args.data,
      headers: tempheaders,
    });

    if (response.ok) {
    } else {
      try {
        let text = await response.json();
        thunkAPI.dispatch(
          addAlert({
            id: crypto.randomUUID(), // eller egen ID-generator
            type: "danger",
            text: text.error || "Error uploading project file",
            status: "danger",
          }),
        );
      } catch (e) {
        thunkAPI.dispatch(
          addAlert({
            id: crypto.randomUUID(), // eller egen ID-generator
            type: "danger",
            text: response.statusText,
            status: "danger",
          }),
        );
      }
    }
    let jsonresp = await response.json();
    args.callback(jsonresp.name);
    thunkAPI.dispatch(fetchUser({ token: args.token }));
  },
);

export const download = createAsyncThunk(
  "projects/download",
  async (args: { guid: string; type: AppType; name: string; token: string }, thunkAPI) => {
    let state = thunkAPI.getState() as UserSliceState;
    const tempheaders = {
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": String(getClientId()),
    };
    const response = await fetch("/api/projects/download", {
      method: "POST",
      body: JSON.stringify({ guid: args.guid, projectType: args.type }),
      headers: { "Content-Type": "application/json", ...tempheaders },
    });
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${args.name}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else {
      thunkAPI.dispatch(
        addAlert({
          id: crypto.randomUUID(), // eller egen ID-generator
          type: "danger",
          text: "Failed to download project",
          status: "danger",
        }),
      );
    }
  },
);

export const addUserToFileProject = createAsyncThunk(
  "project/addUserToFile",
  async (args: { fileguid: string; userids: number; token: string }, thunkAPI) => {
    const tempheaders = {
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": String(getClientId()),
      "Content-Type": "application/json",
    };
    const response = await fetch("/api/projects/addUserToFile", {
      method: "POST",
      body: JSON.stringify({ guid: args.fileguid, userId: args.userids }),
      headers: tempheaders,
    });
    if (!response.ok) {
      thunkAPI.dispatch(
        addAlert({
          id: crypto.randomUUID(),
          type: "danger",
          text: "Failed to add user to file",
          status: "danger",
        }),
      );
      throw new Error("Failed to add user to file");
    }

    thunkAPI.dispatch(fetchUser({ token: args.token }));
  },
);

export const removeUserFromFileProject = createAsyncThunk(
  "project/removeUserFromFileProject",
  async (args: { fileguid: string; userids: number; token: string }, thunkAPI) => {
    const tempheaders = {
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": String(getClientId()),
      "Content-Type": "application/json",
    };
    const response = await fetch("/api/projects/removeUserFromFile", {
      method: "POST",
      body: JSON.stringify({ guid: args.fileguid, userId: args.userids }),
      headers: tempheaders,
    });
    if (!response.ok) {
      thunkAPI.dispatch(
        addAlert({
          id: crypto.randomUUID(),
          type: "danger",
          text: "Failed to remove user from file",
          status: "danger",
        }),
      );
      throw new Error("Failed to remove user from file");
    }

    thunkAPI.dispatch(fetchUser({ token: args.token }));
  },
);

export const update = createAsyncThunk(
  "projects/update",
  async (args: { id: number; meta: any; token: string }, thunkAPI) => {
    let state = thunkAPI.getState() as UserSliceState;
    const tempheaders = {
      "Content-Type": "application/json",
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": String(getClientId()),
    };

    const response = await fetch(`api/projects/${args.id}/`, {
      method: "PUT",
      body: JSON.stringify(args.meta),
      headers: tempheaders,
    });

    if (!response.ok) {
      thunkAPI.dispatch(
        addAlert({
          id: crypto.randomUUID(), // eller egen ID-generator
          type: "danger",
          text: "Failed to update project",
          status: "danger",
        }),
      );
      throw new Error("Failed to update project");
    }
  },
);

export const toggleActivateDeActivateProject = createAsyncThunk(
  "projects/updateActiveDeactiveProject",
  async (args: { guid: string; active: boolean; token: string }, thunkAPI) => {
    let state = thunkAPI.getState() as UserSliceState;
    const tempheaders = {
      "Content-Type": "application/json",
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": String(getClientId()),
    };

    const response = await fetch(`api/projects/updateActiveDeactiveProject`, {
      method: "PUT",
      body: JSON.stringify({ guid: args.guid, active: args.active }),
      headers: tempheaders,
    });
  },
);

export const toggleActiveDeactive = createAsyncThunk(
  "projects/updateActiveDeactive",
  async (args: { projectlist: Array<{ guid: string; type: AppType; active: boolean }>; token: string }, thunkAPI) => {
    let state = thunkAPI.getState() as UserSliceState;
    const tempheaders = {
      "Content-Type": "application/json",
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": String(getClientId()),
    };

    const response = await fetch(`api/projects/updateActiveDeactive`, {
      method: "PUT",
      body: JSON.stringify(args.projectlist),
      headers: tempheaders,
    });

    if (!response.ok) {
      thunkAPI.dispatch(
        addAlert({
          id: crypto.randomUUID(), // eller egen ID-generator
          type: "danger",
          text: "Failed to active/deactive project",
          status: "danger",
        }),
      );
      throw new Error("Failed to active/deactive project");
    }
  },
);

export const changeProjectForFile = createAsyncThunk(
  "projects/changeProject",
  async (args: { projectguid: string; fileguid: string; type: AppType; token: string }, thunkAPI) => {
    let state = thunkAPI.getState() as UserSliceState;
    const tempheaders = {
      "Content-Type": "application/json",
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": String(getClientId()),
    };

    const response = await fetch(`api/projects/changeProjectForFile/`, {
      method: "POST",
      body: JSON.stringify({ projectguid: args.projectguid, fileguid: args.fileguid, type: args.type }),
      headers: tempheaders,
    });
    thunkAPI.dispatch(fetchUser({ token: args.token }));
    if (!response.ok) {
      thunkAPI.dispatch(
        addAlert({
          id: crypto.randomUUID(), // eller egen ID-generator
          type: "danger",
          text: "Failed to change project for file",
          status: "danger",
        }),
      );
      throw new Error("Failed to change project for file");
    }
  },
);

export const createNewEmptyProject = createAsyncThunk(
  "projects/create",
  async (args: { name: string; token: string }, thunkAPI) => {
    let state = thunkAPI.getState() as UserSliceState;
    const tempheaders = {
      "Content-Type": "application/json",
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": getClientId(),
    };

    const response = await fetch(`api/projects/newproject`, {
      method: "POST",
      body: JSON.stringify({
        projectName: args.name,
      }),
      headers: tempheaders,
    });
  },
);

export const create = createAsyncThunk(
  "projects/create",
  async (
    args: {
      name: string;
      type: AppType;
      template: string;
      project: { guid: string; name: string };
      token: string;
      /** Column only: ask the app for a file carrying its example loads instead of an empty one. */
      addDefaultLoads?: boolean;
    },
    thunkAPI,
  ) => {
    console.log(args);
    let state = thunkAPI.getState() as UserSliceState;
    const tempheaders = {
      "Content-Type": "application/json",
      authorization: args.token ? `Bearer ${args.token}` : "",
      "X-Client-ID": getClientId(),
    };

    const meta = ng.frontend.beam.ProjectMeta.create({ name: args.name, project: args.project.name });
    const response = await fetch(`api/projects/`, {
      method: "POST",
      body: JSON.stringify({
        template: args.template,
        type: args.type,
        meta: ng.frontend.beam.ProjectMeta.toObject(meta),
        project: args.project,
        addDefaultLoads: args.addDefaultLoads === true,
      }),
      headers: tempheaders,
    });

    if (!response.ok) {
      let errorText = "Failed to create project";
      try {
        const errorJson = await response.json();
        const details =
          errorJson?.upstreamBody ||
          errorJson?.status ||
          errorJson?.error ||
          errorJson?.message ||
          JSON.stringify(errorJson);
        if (details) {
          errorText = `Failed to create project: ${details}`;
        }
      } catch {
        // If response is not JSON, keep a readable fallback for debugging.
        errorText = `Failed to create project (HTTP ${response.status})`;
      }

      thunkAPI.dispatch(
        addAlert({
          id: crypto.randomUUID(), // eller egen ID-generator
          type: "danger",
          text: errorText,
          status: "danger",
        }),
      );

      throw new Error(errorText);
    }
    thunkAPI.dispatch(fetchUser({ token: args.token }));
  },
);

interface projectState {
  user: any | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  token: string | null;
}

const initialState: projectState = {
  user: null,
  status: "idle",
  token: null,
};

export const ProjectSlice = createAppSlice({
  name: "ProjectSlice",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(create.pending, () => {});
    builder.addCase(create.fulfilled, (state, action) => {
      state.status = "succeeded";
    });
    builder.addCase(create.rejected, (state, action) => {
      state.status = "failed";
    });
  },
});

export function useProjects() {
  const dispatch = useAppDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const deleteProject = async (id: number, guid: string, type: AppType | null) => {
    try {
      let token = await getAccessTokenSilently();
      await dispatch(delProject({ id: id, guid: guid, type: type, token: token }));
    } catch (error) {
      console.error("Failed to delete project:", error);
    }
  };

  const deleteMultipleProjects = async (files: { id: number; type: AppType; guid: string }[]) => {
    try {
      let token = await getAccessTokenSilently();
      await dispatch(delProjects({ projects: files, token: token }));
    } catch (error) {
      console.error("Failed to delete project:", error);
    }
  };

  const deleteProjectandFiles = async (guid: string) => {
    try {
      let token = await getAccessTokenSilently();
      await dispatch(delProjectAndFiles({ projectguid: guid, token: token }));
    } catch (error) {
      console.error("Failed to delete project:", error);
    }
  };

  const updateProject = async (id: number, metadata: Project) => {
    try {
      let token = await getAccessTokenSilently();
      await dispatch(update({ id: id, meta: metadata, token: token }));
    } catch (error) {
      console.error("Failed to delete project:", error);
    }
  };

  const copyProject = async (id: string, guid: string, type: AppType, call: (name: string) => void) => {
    try {
      let token = await getAccessTokenSilently();
      await dispatch(copyProjectFile({ id: id, guid: guid, type: type, token: token, call }));
    } catch (error) {
      console.error("Failed to copy project:", error);
    }
  };

  const downloadProject = async (guid: string, type: AppType, name: string) => {
    try {
      let token = await getAccessTokenSilently();
      await dispatch(download({ guid: guid, type: type, name: name, token: token }));
    } catch (error) {
      console.error("Failed to download project:", error);
    }
  };

  const addUserToFile = async (fileguid: string, userids: number) => {
    try {
      let token = await getAccessTokenSilently();
      await dispatch(addUserToFileProject({ fileguid: fileguid, userids: userids, token: token }));
    } catch (error) {
      console.error("Failed to add user to file:", error);
    }
  };

  const removeUserFromFile = async (fileguid: string, userids: number) => {
    try {
      let token = await getAccessTokenSilently();
      await dispatch(removeUserFromFileProject({ fileguid: fileguid, userids: userids, token: token }));
    } catch (error) {
      console.error("Failed to remove user from file:", error);
    }
  };

  const updateFileMeta = async (id: number, metadata: ProjectFile) => {
    try {
      let token = await getAccessTokenSilently();
      await dispatch(update({ id: id, meta: metadata, token: token }));
    } catch (error) {
      console.error("Failed to delete project:", error);
    }
  };

  const updateActiveDeactive = async (projectList: Array<{ guid: string; type: AppType; active: boolean }>) => {
    try {
      let token = await getAccessTokenSilently();
      await dispatch(toggleActiveDeactive({ projectlist: projectList, token: token }));
    } catch (error) {
      console.error("Failed to update project:", error);
    }
  };

  const updateActiveDeactiveProject = async (project_guid: string, active: boolean) => {
    try {
      let token = await getAccessTokenSilently();
      await dispatch(toggleActivateDeActivateProject({ guid: project_guid, token: token, active: active }));
    } catch (error) {
      console.error("Failed to update project:", error);
    }
  };

  const updateFileProject = async (projectguid: string, fileguid: string, type: AppType) => {
    try {
      await dispatch(
        changeProjectForFile({
          projectguid: projectguid,
          fileguid: fileguid,
          type: type,
          token: await getAccessTokenSilently(),
        }),
      );
    } catch (error) {
      console.error("Failed to update project:", error);
    }
  };

  const createProject = async (
    name: string,
    type: AppType,
    template: ng.material.Mtrl,
    project: { guid: string; name: string },
    addDefaultLoads: boolean = false,
  ) => {
    try {
      let token = await getAccessTokenSilently();
      let materialstr = ng.material.Mtrl_ToObject(template) ?? "fel";
      await dispatch(create({ name: name, type: type, template: materialstr, project, token: token, addDefaultLoads }));
    } catch (error) {
      console.error("Failed to create project:", error);
    }
  };

  const createNewProject = async (name: string) => {
    try {
      let token = await getAccessTokenSilently();
      await dispatch(createNewEmptyProject({ name: name, token: token }));
    } catch (error) {
      console.error("Failed to create project:", error);
    }
  };

  const uploadProjectFile = async (
    file: File,
    projectName: string | null,
    projectGUID: string | null,
    callback: (name: string) => void,
  ) => {
    try {
      let token = await getAccessTokenSilently();
      let formData = new FormData();
      formData.append("file", file);
      await dispatch(
        uploadProject({
          data: formData,
          projectName: projectName,
          projectGUID: projectGUID,
          token: token,
          callback: callback,
        }),
      );
    } catch (error) {
      console.error("Failed to upload project:", error);
    }
  };

  return {
    updateActiveDeactive,
    deleteProject,
    updateProject,
    createProject,
    createNewProject,
    uploadProjectFile,
    updateFileMeta,
    updateFileProject,
    downloadProject,
    copyProject,
    deleteMultipleProjects,
    updateActiveDeactiveProject,
    deleteProjectandFiles,
    addUserToFile,
    removeUserFromFile,
  };
}
